import { formatIDR } from "@marketplace/shared/utils";
import { type DataTableColumn } from "@marketplace/ui/data-table";
import { getRouteApi } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { TransactionStatusPill } from "@/modules/shared";
import { formatWithOffset } from "@/utils";

import type { RecurringOrderListItem } from "../dtos/get-recurring-order-list-response-dto";
import type { OrderStatus } from "../types";
import { convertOrderStatusToStatusPill } from "../utils/order-status";

const route = getRouteApi("/recurring-orders/");

export const useRecurringOrderListTableColumns = () => {
  const { page } = route.useSearch();
  const { t } = useTranslation("order", {
    keyPrefix: "recurringOrderListPage.table.columns"
  });

  const columns = useMemo<DataTableColumn<RecurringOrderListItem>[]>(
    () => [
      {
        accessor: "no",
        title: t("no"),
        render: (_: RecurringOrderListItem, index: number) =>
          (page - 1) * 10 + index + 1
      },
      {
        accessor: "order_id",
        title: t("orderId"),
        render: (record) => record.order_id
      },
      {
        accessor: "name",
        title: t("buyerName"),
        render: (record) => record.customer_name ?? "-"
      },
      {
        accessor: "product_name",
        title: t("productName"),
        render: (record) => record.product_name ?? "-"
      },
      {
        accessor: "package",
        title: t("package"),
        render: (record) => record.package_name ?? "-"
      },
      {
        accessor: "total_payment",
        title: t("totalPayment"),
        render: (record) => formatIDR(record.total_payment)
      },
      {
        accessor: "start_date",
        title: t("startDate"),
        render: (record) => formatWithOffset(record.start_date, "dd/MM/yyyy")
      },
      {
        accessor: "next_billing",
        title: t("nextBilling"),
        render: (record) =>
          record.next_billing
            ? formatWithOffset(record.next_billing, "dd/MM/yyyy")
            : "-"
      },
      {
        accessor: "status",
        title: t("status"),
        render: ({ status }) => {
          const paymentStatus = convertOrderStatusToStatusPill(
            status as OrderStatus
          );

          return <TransactionStatusPill status={paymentStatus} />;
        }
      }
    ],
    [page, t]
  );

  return { columns };
};
